import { useEffect, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import { getOperation } from "../services/api";
import type { IWidgetOperation } from "../interfaces/cash-deposit";

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);
const POLL_INTERVAL_MS = 5_000;

function closeEventSource(
  es: EventSource | null,
  esRef: MutableRefObject<EventSource | null>,
): void {
  if (!es) return;
  es.close();
  if (esRef.current === es) {
    esRef.current = null;
  }
}

export interface UseOperationSSEOptions {
  enabled: boolean;
  linkId: string | null | undefined;
  operationId: string | null | undefined;
  onOperation: (operation: IWidgetOperation) => void;
}

/**
 * Live operation updates via `GET /widget/links/:linkId/operations/sse`.
 * EventSource authenticates off the `:linkId` path param. Heartbeats keep
 * the stream open but do not carry fundingStatus, so the funnel also polls
 * the operation every few seconds. A missed FUNDING_CONFIRMED still closes
 * the barcode.
 */
export function useOperationSSE({
  enabled,
  linkId,
  operationId,
  onOperation,
}: UseOperationSSEOptions): void {
  const esRef = useRef<EventSource | null>(null);
  const onOperationRef = useRef(onOperation);
  onOperationRef.current = onOperation;

  useEffect(() => {
    if (!enabled || !linkId || !operationId) {
      closeEventSource(esRef.current, esRef);
      return;
    }

    if (esRef.current) return;

    let closed = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const stopPoll = (): void => {
      if (!pollTimer) return;
      clearInterval(pollTimer);
      pollTimer = null;
    };

    const applyOperation = (op: IWidgetOperation): void => {
      onOperationRef.current(op);
      if (TERMINAL_STATUSES.has(op.status)) {
        closed = true;
        stopPoll();
        closeEventSource(esRef.current, esRef);
      }
    };

    const pollOnce = (): void => {
      if (closed) return;
      void getOperation(operationId, { raasSilentOffline: true })
        .then((fetched) => {
          if (closed) return;
          applyOperation(fetched);
        })
        .catch((err) =>
          console.error("SSE poll: failed to refresh operation", err),
        );
    };

    pollOnce();
    pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);

    const url = `${config.API_BASE_URL}/widget/links/${linkId}/operations/sse`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (event: MessageEvent) => {
      try {
        const raw = event.data as string;
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (data?.type === "heartbeat") return;
        const updated = data as IWidgetOperation;
        if (!updated?.operationId || updated.operationId !== operationId) return;
        applyOperation(updated);
      } catch (e) {
        console.error("SSE message parse error", e);
      }
    };

    es.onerror = () => {
      console.warn("SSE connection issue — polling until stream recovers", linkId);
    };

    return () => {
      closed = true;
      stopPoll();
      closeEventSource(es, esRef);
    };
  }, [enabled, linkId, operationId]);
}
